import React, { useState, useMemo } from 'react';
import { X, User, Star, MapPin, Mail, Phone, Building2, Calendar, Shield, Check } from 'lucide-react';
import { createInsuranceAppointment } from '../../../Services/APIs/patient';
import { getAvailableTimeSlots, getLocalDateString } from '../../../Utils/timeSlots';

const formatDate = (dateStr) => {
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
};


const RebookModal = ({ booking, email, companyName, onClose, onSuccess }) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [appointmentType, setAppointmentType] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [error, setError] = useState('');

    const minDate = useMemo(() => getLocalDateString(), []);

    const clinic = { working_hours: booking.working_hours, sunday_hours: booking.sunday_hours };
    const availableSlots = selectedDate ? getAvailableTimeSlots(clinic, selectedDate) : [];

    const hasClinicId = Boolean(booking.clinicId);
    const canSubmit = hasClinicId && selectedDate && selectedTime && appointmentType && !isLoading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('clinicId', booking.clinicId);
            formData.append('clinic', JSON.stringify({
                id: booking.clinicId,
                name: booking.clinicName,
                doctor: booking.doctor,
                specialty: booking.clinicSpecialty,
                address: booking.clinicAddress,
                address_2: booking.clinicAddress2,
                city: booking.city,
                state: booking.clinicState,
                pincode: booking.clinicPincode,
                working_hours: booking.working_hours,
                sunday_hours: booking.sunday_hours,
                network_partner: {
                    name: booking.clinicDoctor,
                    experience_years: booking.doctorExperienceYears,
                    phone: booking.doctorPhone,
                    email: booking.doctorEmail,
                },
            }));
            formData.append('client', companyName);
            formData.append('date', selectedDate);
            formData.append('time', selectedTime);
            formData.append('appointment_type', appointmentType);
            formData.append('name', booking.memberName);
            formData.append('email', email);
            formData.append('mobile', booking.memberPhone);
            if (booking.memberId) {
                formData.append('uid', booking.memberId);
            }

            const response = await createInsuranceAppointment(formData);
            if (response.status === 200) {
                const result = {
                    date: formatDate(selectedDate),
                    time: selectedTime,
                    bookingId: response.data?.bookingId,
                };
                setSuccessData(result);
                setIsSuccess(true);
            } else {
                setError('Internal server error. Please try again.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to book. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">

                {isSuccess ? (
                    <div className="flex flex-col max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-gray-800 font-bold text-lg mb-1">Appointment Booked</h3>
                            <p className="text-gray-400 text-sm mb-4">Your appointment has been successfully booked.</p>
                        </div>

                        {/* Appointment Details */}
                        <div className="rounded-2xl p-4 w-full mb-4 bg-gray-50 border border-gray-100">
                            <h4 className="font-bold mb-3 flex items-center text-sm text-gray-800"><Calendar className="w-4 h-4 mr-2 text-indigo-500" />Appointment Details</h4>
                            <div className="space-y-2.5">
                                {[
                                    ['Patient', booking.memberName],
                                    ['Clinic', <><span className="font-semibold">{booking.clinicName}</span><br /><span className="text-xs text-gray-400">{booking.doctor || booking.clinicDoctor}</span></>],
                                    ['Date', successData?.date],
                                    ['Time', <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold">{successData?.time}</span>],
                                    ['Type', <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold capitalize">{appointmentType}</span>],
                                ].map(([label, val], i) => (
                                    <div key={label}>
                                        <div className="flex justify-between items-start">
                                            <span className="text-xs text-gray-400">{label}</span>
                                            <span className="font-semibold text-sm text-right text-gray-800">{val}</span>
                                        </div>
                                        {i < 4 && <div className="border-t border-gray-100 mt-2" />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="rounded-2xl p-4 w-full mb-4 bg-indigo-50 border border-indigo-100">
                            <h4 className="font-semibold mb-3 text-sm flex items-center text-indigo-900"><Phone className="w-4 h-4 mr-2" />Contact Information</h4>
                            <div className="space-y-2 text-sm">
                                <div className="rounded-xl p-3 space-y-1.5 bg-white bg-opacity-60">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Your Details</p>
                                    <div className="flex items-center text-gray-700"><Mail className="w-3.5 h-3.5 mr-2 text-indigo-400" /><span className="text-xs">{email}</span></div>
                                    <div className="flex items-center text-gray-700"><Phone className="w-3.5 h-3.5 mr-2 text-indigo-400" /><span className="text-xs">{booking.memberPhone}</span></div>
                                </div>
                                <div className="rounded-xl p-3 space-y-1.5 bg-white bg-opacity-60">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Clinic</p>
                                    {booking.clinicName && <div className="flex items-center text-gray-700"><Building2 className="w-3.5 h-3.5 mr-2 text-indigo-400" /><span className="text-xs">{booking.clinicName}</span></div>}
                                    {[booking.clinicAddress, booking.clinicAddress2].filter(Boolean).length > 0 && (
                                        <div className="flex items-center text-gray-700"><MapPin className="w-3.5 h-3.5 mr-2 text-indigo-400" /><span className="text-xs">{[booking.clinicAddress, booking.clinicAddress2].filter(Boolean).join(', ')}</span></div>
                                    )}
                                    {[booking.city, booking.clinicState, booking.clinicPincode].filter(Boolean).length > 0 && (
                                        <div className="flex items-center text-gray-700"><MapPin className="w-3.5 h-3.5 mr-2 text-indigo-400" /><span className="text-xs">{[booking.city, booking.clinicState, booking.clinicPincode].filter(Boolean).join(', ')}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Important Notes */}
                        <div className="rounded-2xl p-4 w-full mb-5 bg-amber-50 border border-amber-100">
                            <h4 className="font-semibold mb-2 text-sm flex items-center text-amber-800"><Shield className="w-4 h-4 mr-2" />Important Notes</h4>
                            <ul className="text-xs space-y-1.5 text-amber-700">
                                <li className="flex items-start"><span className="mr-2">●</span>Please arrive 15 minutes before your appointment</li>
                                <li className="flex items-start"><span className="mr-2">●</span>Please show your ToothLens UID at the clinic</li>
                                <li className="flex items-start"><span className="mr-2">●</span>Contact ToothLens for appointment assistance</li>
                            </ul>
                        </div>

                        <button
                            onClick={() => onSuccess(successData)}
                            className="w-full py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="bg-indigo-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h3 className="text-white font-bold text-base">Rebook Appointment</h3>
                                <p className="text-indigo-200 text-xs mt-0.5 truncate">{booking.clinicName}</p>
                            </div>
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all flex-shrink-0 ml-3"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {/* Clinic Info */}
                            <div className="bg-indigo-50 rounded-xl p-3 mb-4">
                                <p className="font-bold text-sm text-indigo-900">{booking.clinicName}</p>
                                <div className="space-y-0.5 mt-1 text-xs text-indigo-600">
                                    {(booking.doctor || booking.clinicDoctor) && (
                                        <p className="flex items-center"><User className="w-3 h-3 mr-1" />{booking.doctor || booking.clinicDoctor}</p>
                                    )}
                                    {booking.doctorExperienceYears && (
                                        <p className="flex items-center"><Star className="w-3 h-3 mr-1" />{booking.doctorExperienceYears} years exp.</p>
                                    )}
                                    {[booking.clinicAddress, booking.clinicAddress2].filter(Boolean).length > 0 && (
                                        <p className="flex items-center"><MapPin className="w-3 h-3 mr-1 flex-shrink-0" /><span className="truncate">{[booking.clinicAddress, booking.clinicAddress2].filter(Boolean).join(', ')}</span></p>
                                    )}
                                    {[booking.city, booking.clinicState, booking.clinicPincode].filter(Boolean).length > 0 && (
                                        <p className="flex items-center"><MapPin className="w-3 h-3 mr-1 flex-shrink-0" />{[booking.city, booking.clinicState, booking.clinicPincode].filter(Boolean).join(', ')}</p>
                                    )}
                                </div>
                            </div>

                            {/* Patient Details */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="rounded-xl p-3 bg-gray-50 border border-gray-100">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Name</p>
                                    <p className="font-medium text-sm text-gray-800 mt-1">{booking.memberName || '—'}</p>
                                </div>
                                <div className="rounded-xl p-3 bg-gray-50 border border-gray-100">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Mobile</p>
                                    <p className="font-medium text-sm text-gray-800 mt-1">{booking.memberPhone || '—'}</p>
                                </div>
                            </div>

                            {!hasClinicId && (
                                <p className="text-red-500 text-xs mb-3 text-center">
                                    Unable to rebook — clinic details unavailable for this appointment.
                                </p>
                            )}

                            <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Appointment Type</p>
                            <select
                                value={appointmentType}
                                onChange={(e) => setAppointmentType(e.target.value)}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 mb-5 cursor-pointer bg-white"
                            >
                                <option value="" disabled>Select appointment type</option>
                                <option value="consultation">Consultation</option>
                                <option value="procedure">Procedure</option>
                            </select>

                            <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Select Date</p>
                            <input
                                type="date"
                                min={minDate}
                                value={selectedDate}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v < minDate) { window.alert('Past dates cannot be booked.'); setSelectedDate(''); setSelectedTime(''); }
                                    else { setSelectedDate(v); setSelectedTime(''); }
                                }}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 mb-5 cursor-pointer"
                            />

                            <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-2">Select Time</p>
                            <div className="relative">
                                <select
                                    value={selectedTime}
                                    onChange={(e) => setSelectedTime(e.target.value)}
                                    disabled={availableSlots.length === 0}
                                    className="w-full appearance-none border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="" disabled>
                                        {selectedDate && availableSlots.length === 0 ? 'No slots available' : 'Choose a time'}
                                    </option>
                                    {availableSlots.map((slot) => (
                                        <option key={slot} value={slot}>{slot}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            {error && (
                                <p className="text-red-500 text-xs mt-3 text-center">{error}</p>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 flex-shrink-0">
                            <button
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className="w-full py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
                            >
                                {isLoading ? 'Booking…' : 'Confirm Rebook'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RebookModal;
